import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Volume2, BellRing, BellOff, Radar } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { playAlert, unlockAudio } from "@/lib/sound";
import { enablePush, pushPermissionState } from "@/lib/push";
import { getSettings, updateSettings } from "@/lib/api";

export default function Header({ freeCount, matchCount }) {
  const qc = useQueryClient();
  const [pushState, setPushState] = useState("default");
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  const [radius, setRadius] = useState(80);

  useEffect(() => {
    setPushState(pushPermissionState());
  }, []);
  useEffect(() => {
    if (settings?.matching_radius_km) setRadius(settings.matching_radius_km);
  }, [settings]);

  const radiusMut = useMutation({
    mutationFn: (v) => updateSettings(v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings"] }),
  });

  const handleTestSound = () => {
    unlockAudio();
    playAlert();
    toast.info("Sunet de alertă redat pe acest PC 🔊");
  };

  const handleEnablePush = async () => {
    try {
      unlockAudio();
      await enablePush();
      setPushState("granted");
      toast.success("Notificările push pe telefon sunt ACTIVE ✔");
    } catch (e) {
      toast.error(e.message || "Nu am putut activa notificările");
    }
  };

  return (
    <header className="sticky top-0 z-30 bg-[#0B0F17]/90 backdrop-blur-md border-b border-[#2A364F]">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-3 mr-auto">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/65402f7f-edc3-4701-9cf2-4274942fe0aa/images/e5a630e9b59a6aaa55e680e0b585449c51d565eecd8fedd67de8baa2e589c39a.jpeg"
            alt="logo"
            className="w-11 h-11 rounded-xl border border-[#2A364F]"
          />
          <div>
            <h1 className="font-display text-xl sm:text-2xl font-extrabold tracking-tight leading-none">
              DISPECERAT
            </h1>
            <p className="text-xs text-slate-400 font-mono tracking-wider">POTRIVIRE CURSE · LIVE</p>
          </div>
        </div>

        <div className="flex items-center gap-2 text-sm">
          <span
            data-testid="free-count-badge"
            className="px-3 py-1.5 rounded-lg bg-green-900/40 text-green-400 border border-green-700 font-semibold"
          >
            {freeCount} Libere
          </span>
          <span
            data-testid="match-count-badge"
            className="px-3 py-1.5 rounded-lg bg-indigo-900/40 text-indigo-300 border border-indigo-600 font-semibold"
          >
            {matchCount} Potriviri
          </span>
        </div>

        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#151C28] border border-[#2A364F] min-w-[220px]">
          <Radar className="w-5 h-5 text-blue-400 shrink-0" />
          <div className="flex-1">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-400">Rază potrivire</span>
              <span className="font-mono font-bold text-blue-300">{radius} km</span>
            </div>
            <Slider
              data-testid="matching-radius-slider"
              value={[radius]}
              min={10}
              max={500}
              step={10}
              onValueChange={(v) => setRadius(v[0])}
              onValueCommit={(v) => radiusMut.mutate(v[0])}
            />
          </div>
        </div>

        <Button
          data-testid="sound-test-btn"
          onClick={handleTestSound}
          variant="outline"
          className="h-11 border-[#2A364F] bg-[#151C28] hover:bg-[#1E293B] text-base font-semibold"
        >
          <Volume2 className="w-5 h-5 mr-2" /> Test Sunet
        </Button>

        <Button
          data-testid="enable-push-btn"
          onClick={handleEnablePush}
          className={
            "h-11 text-base font-semibold " +
            (pushState === "granted"
              ? "bg-green-600 hover:bg-green-700"
              : "bg-blue-600 hover:bg-blue-700")
          }
        >
          {pushState === "granted" ? (
            <>
              <BellRing className="w-5 h-5 mr-2" /> Push Activ
            </>
          ) : (
            <>
              <BellOff className="w-5 h-5 mr-2" /> Activează Push
            </>
          )}
        </Button>
      </div>
    </header>
  );
}
